import type { InventoryComponentKey } from 'lib/modules';

/**
 * The Orders page's tabs and their routes. Kept apart from the page so the app
 * shell's nav can link to it without pulling the (lazy-loaded) page into the
 * main bundle.
 */

export const ORDERS_PATH = '/processor/orders';

/**
 * Production: work orders, the batches they pool into, and the floor board —
 * one page, three views. It keeps the work-order list's old path because every
 * work order's detail page lives beneath it.
 */
export const PRODUCTION_PATH = '/processor/further-processing';
/** Kept for callers that mean "the work-order list"; it's the Production page. */
export const JOBS_PATH = PRODUCTION_PATH;

/**
 * Purchasing: purchase orders and the suppliers they're placed with.
 * `?sku=<id>` on it opens a new purchase order for that product.
 */
export const PURCHASING_PATH = '/processor/orders/purchasing';
/** Purchasing's purchase-order view, which takes the `?sku=` deep link. */
export const SOURCING_PATH = PURCHASING_PATH;

/**
 * Which of a tabbed page's views is showing, as a query param rather than a
 * path so the views share one route (and its licence gate) and a detail
 * page's Back returns to the view it was opened from.
 */
export const VIEW_PARAM = 'view';

export type ProductionView = 'list' | 'batches' | 'board';
export type PurchasingView = 'orders' | 'suppliers';

/**
 * A Production view's path. The list is the default and carries no param.
 * @param view - The view
 * @returns The path
 */
export function productionPath(view: ProductionView = 'list'): string {
  return view === 'list'
    ? PRODUCTION_PATH
    : `${PRODUCTION_PATH}?${VIEW_PARAM}=${view}`;
}

/**
 * A Purchasing view's path. Purchase orders are the default and carry no param.
 * @param view - The view
 * @returns The path
 */
export function purchasingPath(view: PurchasingView = 'orders'): string {
  return view === 'orders'
    ? PURCHASING_PATH
    : `${PURCHASING_PATH}?${VIEW_PARAM}=${view}`;
}

/**
 * Production grouped by batch — where the batches list used to live, and where
 * a batch's detail page goes Back to.
 */
export const BATCHES_PATH = productionPath('batches');
/** Production's floor board — the old standalone dashboard. */
export const DASHBOARD_PATH = productionPath('board');
/** Purchasing's supplier directory. */
export const SUPPLIERS_PATH = purchasingPath('suppliers');

/**
 * Paths that no longer render a page of their own but are bookmarked, so the
 * router redirects them (keeping any query string, e.g. `?sku=` or
 * `?create=`) to where their content lives now.
 */
export const LEGACY_REDIRECTS: { from: string; to: string }[] = [
  { from: '/processor/production-batches', to: BATCHES_PATH },
  { from: '/processor/production-batches/dashboard', to: DASHBOARD_PATH },
  { from: '/processor/orders/sourcing', to: PURCHASING_PATH },
  { from: '/processor/inventory/sourcing', to: PURCHASING_PATH },
  { from: '/processor/inventory/management/suppliers', to: SUPPLIERS_PATH },
];

/**
 * Where a legacy redirect lands: its target, with the query string it was
 * reached with carried over (the target's own params win, so `?view=` can't
 * be overridden, but `?sku=` / `?create=` ride along).
 * @param to - The redirect's target, possibly with its own query
 * @param search - The query string the old URL was opened with
 * @returns The target's pathname and search
 */
export function legacyRedirectTarget(
  to: string,
  search: string,
): { pathname: string; search: string } {
  const [pathname, targetQuery = ''] = to.split('?');
  const params = new URLSearchParams(search);
  new URLSearchParams(targetQuery).forEach((value, key) =>
    params.set(key, value),
  );
  const query = params.toString();
  return { pathname, search: query ? `?${query}` : '' };
}

export type OrdersTabKey = 'orders' | 'production' | 'purchasing';

export interface OrdersTab {
  key: OrdersTabKey;
  label: string;
  path: string;
  /**
   * The inventory module component(s) the tab belongs to — any one of them
   * shows it.
   */
  component: InventoryComponentKey | InventoryComponentKey[];
}

/**
 * Whether the vendor licenses a tab.
 * @param tab - The tab
 * @param has - Whether the vendor has an inventory component
 * @returns True when any of the tab's components is on
 */
export function hasOrdersTab(
  tab: OrdersTab,
  has: (component: InventoryComponentKey) => boolean,
): boolean {
  const components = Array.isArray(tab.component)
    ? tab.component
    : [tab.component];
  return components.some((c) => has(c));
}

/**
 * The work that moves product: customer orders going out, the production that
 * makes what they asked for, and purchasing bringing stock in. Each is a
 * separately licensed component, so the tabs shown are whichever the vendor
 * has.
 */
export const ORDERS_TABS: OrdersTab[] = [
  // Customer orders — retail and custom processing need stock; wholesale
  // doesn't, so a kill-to-order plant gets the page with wholesale alone.
  {
    key: 'orders',
    label: 'Orders',
    path: ORDERS_PATH,
    component: ['orders', 'wholesaleOrders'],
  },
  {
    key: 'production',
    label: 'Further Processing',
    path: PRODUCTION_PATH,
    component: 'furtherProcessing',
  },
  {
    key: 'purchasing',
    label: 'Purchasing',
    path: PURCHASING_PATH,
    component: 'sourcingAndCosting',
  },
];

/**
 * Which tab a path belongs to. Purchasing is checked before Orders because it
 * lives under the orders path.
 * @param pathname - The current path
 * @returns The tab key
 */
export function tabForPath(pathname: string): OrdersTabKey {
  if (pathname.startsWith(PRODUCTION_PATH)) return 'production';
  if (pathname.startsWith(PURCHASING_PATH)) return 'purchasing';
  return 'orders';
}

/**
 * The first Orders-area path this vendor can open, for the nav item: customer
 * orders when they have them, else whichever of the other tabs they license.
 * @param has - Whether the vendor has an inventory component
 * @returns The path, or null when they have none of them
 */
export function ordersEntryPath(
  has: (component: InventoryComponentKey) => boolean,
): string | null {
  return ORDERS_TABS.find((t) => hasOrdersTab(t, has))?.path ?? null;
}
